import clsx from "clsx";
import { CheckIcon } from "@heroicons/react/24/outline";
import type { RankedTag } from "@/shared/utils/tagSearch";

/**
 * The line that tells people the rest of the taxonomy is reachable.
 *
 * Both panels list only what the user owns plus the ~51 curated menu tags, and
 * nothing on screen said the other tens of thousands existed — so a trader who
 * couldn't see "OPEC" concluded Newsquawk didn't tag it.
 */
export const TAG_LIBRARY_HINT =
  "Don't see the tag you want? Type it above to search all Newsquawk tags.";

interface TagLibraryResultsProps {
  tags: RankedTag[];
  isLoading: boolean;
  isEmpty: boolean;
  query: string;
  /** Tag ids already selected, so a repeat pick reads as ticked. */
  selectedIds: string[];
  onToggle: (tagId: string, name: string) => void;
  /** Ids already listed by the panel's own sections; hidden to avoid doubles. */
  hiddenIds?: Set<string>;
  className?: string;
}

export default function TagLibraryResults({
  tags,
  isLoading,
  isEmpty,
  query,
  selectedIds,
  onToggle,
  hiddenIds,
  className,
}: TagLibraryResultsProps) {
  const visible = hiddenIds
    ? tags.filter((t) => !hiddenIds.has(String(t.tag_id)))
    : tags;

  if (!isLoading && !isEmpty && visible.length === 0) return null;

  return (
    <div className={clsx("border-b border-gray-700/30", className)}>
      <div className="px-3 py-1.5 flex items-center gap-2">
        <h3 className="text-2xs font-semibold uppercase tracking-wider text-gray-500">
          From the tag library
        </h3>
        {isLoading && (
          <span className="text-2xs text-gray-600">searching…</span>
        )}
      </div>

      {isEmpty && !isLoading ? (
        <p className="px-3 pb-2 text-xs text-gray-500">
          No Newsquawk tag matches &quot;{query}&quot;.
        </p>
      ) : (
        <ul className="pb-1">
          {visible.map((tag) => {
            const id = String(tag.tag_id);
            const isSelected = selectedIds.includes(id);
            return (
              <li key={id}>
                <button
                  type="button"
                  onClick={() => onToggle(id, tag.name)}
                  className="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-gray-700/40 transition-colors focus:outline-none"
                >
                  <span
                    className={clsx(
                      "size-3.5 flex-shrink-0 rounded-sm border flex items-center justify-center",
                      isSelected
                        ? "bg-squawk-blue border-squawk-blue"
                        : "border-gray-600"
                    )}
                  >
                    {isSelected && <CheckIcon className="size-2.5 text-white" />}
                  </span>
                  <span className="flex-1 text-xs text-gray-200 truncate">
                    {tag.name}
                  </span>
                  {/* The label that actually matched, when it isn't the name
                      shown — otherwise searching "AAPL" lists "Apple Inc" with
                      no visible reason for the hit. */}
                  {tag.matchedLabel !== tag.name && (
                    <span className="text-2xs text-gray-500 truncate max-w-[40%]">
                      {tag.matchedLabel}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
